import './TeamCreateStepPlayers.scss';

import { Grid } from '@mui/material';
import classNames from 'classnames';
import { IEventRepository } from 'domain/event/Event.repository';
import { PlayerRoleEnum } from 'domain/event/PlayerRoleEnum';
import { UnpyEvent } from 'domain/event/UnpyEvent';
import { UnpyEventView } from 'domain/event/UnpyEventView';
import { Team } from 'domain/team/Team';
import { CoupleButtonValidCancel } from 'primary/Components/CoupleButtonValidCancel/CoupleButtonValidCancel';
import { CreateTeamPlayerFields } from 'primary/Components/Event/CreateTeamPlayerFields';
import { sendEventToastMessage } from 'primary/Components/Toast/Toast.helper';
import { useContextDependency } from 'primary/hooks/useContextDependency';
import { useTranslate, UseTranslateReturn } from 'primary/hooks/useTranslate';
import { useYupValidationResolver } from 'primary/hooks/useYupValidationResolver';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import * as yup from 'yup';

export type FormAddMissingPlayerData = {
  idTeam: number;
  idEvent: number;
  players: {
    [key: string]: CompletePlayerItemFormData;
  };
};

export type CompletePlayerItemFormData = {
  idPlayer?: string;
  role?: PlayerRoleEnum;
  withLink?: boolean;
  idPlayerInTeam?: number;
};

const validationSchema = (team: Team) => (t: UseTranslateReturn) =>
  yup.object({
    idEvent: yup.string().required(),
    idTeam: yup.string().required(),
    players: yup.object({
      ...team.players
        .filter((p) => !p.player && !p.invitationLink)
        .reduce((acc, value, currentIndex) => {
          return {
            ...acc,
            [`player-${currentIndex}`]: yup.object({
              idPlayerInTeam: yup.string().required(t('general.forms.errors.required')),
              idPlayer: yup.string().when(`withLink`, {
                is: (val?: boolean) => !val || val?.toString() === 'false',
                then: yup.string().required(t('general.forms.errors.required')),
                otherwise: yup.string().nullable(),
              }),
              role: yup.mixed().required(t('general.forms.errors.required')),
            }),
          };
        }, {}),
    }),
  });

const useOnSubmit =
  (
    eventRepository: IEventRepository,
    setLoading: (teamCreatingLoading: boolean) => void,
    onSubmitted: () => void,
    idEvent: number,
    t: UseTranslateReturn,
  ) =>
  (data: FormAddMissingPlayerData) => {
    setLoading(true);
    eventRepository
      .completeTeam(data)
      .then(() => {
        sendEventToastMessage(t('general.apiResult.success'), 'success');
        onSubmitted();
      })
      .finally(() => {
        setLoading(false);
      });
  };

type FormAddMissingPlayerProps = {
  team: Team;
  event: UnpyEvent | UnpyEventView;
  onSubmitted: () => void;
  onCancel: () => void;
};

export const getnitialValuesPlayersFromTeam = (team: Team) => {
  const missingPlayer = team.players
    .filter((p) => !p.player && !p.invitationLink)
    .sort((a, b) => a.id - b.id);
  return missingPlayer.reduce((acc, value, currentIndex) => {
    return {
      ...acc,
      [`player-${currentIndex}`]: {
        idPlayer: undefined,
        role: PlayerRoleEnum.TITULAR.toString(),
        idPlayerInTeam: value.id,
      },
    };
  }, {});
};

export const FormAddMissingPlayer = ({
  team,
  event,
  onSubmitted,
  onCancel,
}: FormAddMissingPlayerProps) => {
  const { authRepository, eventRepository } = useContextDependency();
  const resolver = useYupValidationResolver(validationSchema(team));

  const form = useForm<FormAddMissingPlayerData>({
    resolver,
    defaultValues: {
      idTeam: team.id,
      idEvent: event.id,
      players: {
        ...getnitialValuesPlayersFromTeam(team),
      },
    },
  });

  const {
    handleSubmit,
    control,
    setValue,
    trigger,
    register,
    getValues,
    watch,
    setError,
    formState: { isSubmitting, errors },
  } = form;
  console.log('ERRORS', form.formState.errors);
  const t = useTranslate();
  const dispatch = useDispatch();
  const players = watch('players');
  const [teamCompletionLoading, setTeamCompletionLoading] = useState(false);
  const onSubmit = useOnSubmit(
    eventRepository,
    setTeamCompletionLoading,
    onSubmitted,
    event.id,
    t,
  );
  const missingPlayers = team.players.filter((p) => !p.player && !p.invitationLink);

  return (
    <div className={classNames('teamCreateStepPlayers')}>
      <h4>{t('event.register.choiceAddPlayer')}</h4>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className={'-playersField'}>
          {missingPlayers.map((value, index) => (
            <CreateTeamPlayerFields
              forceCanLinkInvitation
              key={index}
              //@ts-ignore
              form={form}
              index={index}
              structureId={undefined}
              disabledPlayer={false}
              onlyTitular
              eventId={event.id}
              creatorIdPlayer={event.creator.idPlayer}
              idPlayersToExclude={team.players
                .filter((player) => !!player.player)
                .map((player) => player.player?.idPlayer as string)}
            />
          ))}
        </div>
        <Grid container>
          <Grid item xs={12}>
            <CoupleButtonValidCancel
              onClickCancel={onCancel}
              loading={teamCompletionLoading}
              isTypeSubmit
            />
          </Grid>
        </Grid>
      </form>
    </div>
  );
};
